import type { Event } from '@viet-parenting/supabase/types';
import { pick, t } from '../i18n';
import { countryName, getCountryByCode } from '../countries';
import { isOnlineEvent, safeTimeZone } from '../events';
import { ReportLink } from './parts';
import type { DetailContext } from './types';

interface Props {
  event: Event;
  ctx: DetailContext;
}

export default function EventDetail({ event, ctx }: Props) {
  const { locale, hrefs } = ctx;
  const name = pick(locale, event.name_vi, event.name_en);
  const description = pick(locale, event.description_vi ?? '', event.description_en);
  const isOnline = isOnlineEvent(event);
  const country = getCountryByCode(event.venue_country);
  const tz = safeTimeZone(event.timezone);
  const dateLocale = locale === 'vi' ? 'vi-VN' : 'en-GB';

  const startsAt = new Date(event.starts_at).toLocaleString(dateLocale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: tz,
  });
  const endsAt = new Date(event.ends_at).toLocaleTimeString(dateLocale, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: tz,
  });

  const photos = event.photos ?? [];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <nav className="text-sm text-gray-400 mb-6">
        {hrefs.home && (
          <>
            <a href={hrefs.home} className="hover:text-red-700">
              Home
            </a>
            {' / '}
          </>
        )}
        <a href={hrefs.back} className="hover:text-red-700">
          {t(locale, 'events')}
        </a>
        {' / '}
        <span className="text-gray-700">{name}</span>
      </nav>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
        <article className="md:col-span-2">
          {photos.length > 0 && (
            <div className="mb-6 space-y-2">
              <div className="rounded-xl overflow-hidden">
                <img
                  src={ctx.imageUrl(photos[0]!, 'public')}
                  alt={name}
                  className="w-full h-72 object-cover"
                />
              </div>
              {photos.length > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {photos.slice(1).map((photoId, i) => (
                    <div className="rounded-lg overflow-hidden aspect-square" key={photoId}>
                      <img
                        src={ctx.imageUrl(photoId, 'thumbnail')}
                        alt={`${name} ${i + 2}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <span className="inline-block text-xs font-semibold uppercase tracking-wide text-red-700 mb-2">
            {isOnline ? (locale === 'vi' ? 'Trực tuyến' : 'Online') : event.venue_city}
          </span>

          {/* Prerendered pages outlive the event, so "past" is decided in the
              browser by attachDetailBehavior rather than at build time. */}
          <p className="hidden mb-2" data-vp-past-badge data-ends-at={event.ends_at}>
            <span className="inline-flex items-center text-xs font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full bg-gray-100 text-gray-500">
              {t(locale, 'past_event')}
            </span>
          </p>

          <h1 className="font-display text-4xl font-extrabold text-red-700 mb-4">{name}</h1>

          <div className="flex flex-wrap gap-2 mb-6">
            {event.age_groups.map((a) => (
              <span
                className="text-xs bg-red-50 text-red-700 px-3 py-1 rounded-full font-semibold"
                key={a}
              >
                {t(locale, a)}
              </span>
            ))}
          </div>

          {description && (
            <div
              className="rich-content prose-sm max-w-none text-gray-700"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
        </article>

        <aside className="space-y-4">
          <div className="brand-card p-5">
            <p className="text-xs text-gray-400 mb-2 uppercase tracking-wide">
              {locale === 'vi' ? 'Thời gian' : 'Date & time'}
            </p>
            <p className="font-semibold text-sm">{startsAt}</p>
            <p className="text-xs text-gray-400 mt-1">
              {locale === 'vi' ? 'đến' : 'until'} {endsAt}
            </p>
          </div>

          <div className="brand-card p-5">
            <p className="text-xs text-gray-400 mb-2 uppercase tracking-wide">
              {locale === 'vi' ? 'Địa điểm' : 'Location'}
            </p>
            {isOnline ? (
              <p className="text-sm font-semibold">
                🌐 {locale === 'vi' ? 'Trực tuyến' : 'Online'}
              </p>
            ) : (
              <>
                <p className="font-semibold text-sm">
                  📍{' '}
                  {[event.venue_city, country ? countryName(country, locale) : null]
                    .filter(Boolean)
                    .join(', ')}
                </p>
                {event.venue_address && (
                  <p className="text-xs text-gray-500 mt-1">{event.venue_address}</p>
                )}
              </>
            )}
          </div>

          <ReportLink ctx={ctx} className="block text-center pt-1" />
        </aside>
      </div>
    </div>
  );
}
